'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { STALE_TIMES, CACHE_TIMES } from '@/providers/QueryProvider'

// =====================================
// Query Keys
// =====================================
export const calendarKeys = {
  all: ['calendar'] as const,
  
  // Appointments
  appointments: (userId: string | undefined, clinicId: string | undefined) => 
    [...calendarKeys.all, 'appointments', userId, clinicId] as const,
  appointmentsWithRange: (userId: string | undefined, clinicId: string | undefined, startDate: string, endDate: string) => 
    [...calendarKeys.appointments(userId, clinicId), startDate, endDate] as const,
  
  // Single appointment
  appointment: (id: string) => 
    [...calendarKeys.all, 'appointment', id] as const,
}

// =====================================
// Types
// =====================================
interface Appointment {
  id: string
  patient_id: string
  user_id: string
  clinic_id: string
  appointment_date: string
  start_time: string
  end_time: string
  visit_type: string
  status: string
  notes?: string
  chief_complaint?: string
  patient?: {
    first_name: string
    last_name: string
  }
  user?: {
    full_name: string
  }
}

interface AppointmentsResponse {
  success: boolean
  appointments: Appointment[]
}

// =====================================
// Fetch Functions
// =====================================
async function fetchAppointments(
  userId: string | undefined,
  clinicId: string | undefined,
  startDate: string,
  endDate: string
): Promise<AppointmentsResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  })
  if (userId) params.append('user_id', userId)
  if (clinicId) params.append('clinic_id', clinicId)

  const response = await fetch(`/api/calendar/appointments?${params}`)
  if (!response.ok) {
    throw new Error('Failed to fetch appointments')
  }
  return response.json()
}

// =====================================
// Query Hooks
// =====================================

/**
 * Fetch appointments with React Query caching
 */
export function useAppointments(
  userId: string | undefined,
  clinicId: string | undefined,
  startDate: string,
  endDate: string,
  enabled = true
) {
  return useQuery({
    queryKey: calendarKeys.appointmentsWithRange(userId, clinicId, startDate, endDate),
    queryFn: () => fetchAppointments(userId, clinicId, startDate, endDate),
    enabled: enabled && !!startDate && !!endDate,
    staleTime: STALE_TIMES.CALENDAR,
    gcTime: CACHE_TIMES.MEDIUM,
  })
}

/**
 * Create appointment mutation
 */
export function useCreateAppointment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      patient_id: string
      user_id: string
      clinic_id: string
      appointment_date: string
      start_time: string
      end_time: string
      visit_type: string
      notes?: string
      chief_complaint?: string
    }) => {
      const response = await fetch('/api/calendar/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to create appointment')
      return response.json()
    },
    onSuccess: (_, variables) => {
      // Invalidate appointments cache
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointments(variables.user_id, variables.clinic_id)
      })
    }
  })
}

/**
 * Update appointment mutation
 */
export function useUpdateAppointment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      id: string
      user_id?: string
      clinic_id?: string
      status?: string
      notes?: string
      start_time?: string
      end_time?: string
    }) => {
      const response = await fetch(`/api/calendar/appointments/${data.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (!response.ok) throw new Error('Failed to update appointment')
      return response.json()
    },
    onSuccess: (_, variables) => {
      // Invalidate specific appointment and list
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointment(variables.id)
      })
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointments(variables.user_id, variables.clinic_id)
      })
    }
  })
}

/**
 * Delete appointment mutation
 */
export function useDeleteAppointment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: { id: string; user_id?: string; clinic_id?: string }) => {
      const response = await fetch(`/api/calendar/appointments/${data.id}`, {
        method: 'DELETE'
      })
      if (!response.ok) throw new Error('Failed to delete appointment')
      return response.json()
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointments(variables.user_id, variables.clinic_id)
      })
    }
  })
}

/**
 * Prefetch appointments for date range
 */
export function usePrefetchAppointments() {
  const queryClient = useQueryClient()
  
  return (userId: string | undefined, clinicId: string | undefined, startDate: string, endDate: string) => {
    queryClient.prefetchQuery({
      queryKey: calendarKeys.appointmentsWithRange(userId, clinicId, startDate, endDate),
      queryFn: () => fetchAppointments(userId, clinicId, startDate, endDate),
      staleTime: STALE_TIMES.CALENDAR,
    })
  }
}
